'use client';

import { useCallback, useEffect } from 'react';

import { logger } from '../../../../core/Log/logger';
import {
    useAmplitude,
    useTrackEventAmplitude,
} from '../../../../core/Tracking/Amplitude/Amplitude';
import { User } from '../../../../resources/user/domain/types/User';
import { trackOptin } from '../../../../shared/components/Newsletter/NewsletterService';
import { useUser } from '../../../../shared/providers/user/useUser';
import { PickDomain } from '../../../../shared/utilities/types/PickDomain';
import { OnLogin, RegisterOptions } from '../../Login';
import { SocialLoginProviderName } from '../Provider';

import { useGoogleScript, WindowGoogle } from './Google';

export function GoogleOneTap() {
    const { isUserConnectedOnLoad } = useUser();
    const { setIsConnected } = useAmplitude();
    const { trackEvent } = useTrackEventAmplitude();

    const onRegister = useCallback(
        (loginProviderName: SocialLoginProviderName, options?: RegisterOptions) => {
            void trackOptin({
                isNew: true,
                subscribeMode: '',
                subscribeModeDetail: '',
                email: options?.email ?? '',
            });

            trackEvent((ampli, defaultProperties) => {
                ampli.authenticationSucceed({
                    ...defaultProperties,
                    already_existing: false,
                    connection_source: 'google-one-tap',
                });
            });
        },
        [trackEvent],
    );

    const onLogin: OnLogin = useCallback(
        (
            loginProviderName,
            user: PickDomain<
                User,
                | 'accountId'
                | 'isEvaneos'
                | 'recommendationsIsEnabled'
                | 'numberOfUserQuotesByStateCounters',
                {
                    status: User['status'] | null;
                }
            >,
        ) => {
            trackEvent((ampli, defaultProperties) => {
                ampli.authenticationSucceed({
                    ...defaultProperties,
                    already_existing: true,
                    connection_source: 'google-one-tap',
                });
            });

            if (user) {
                setIsConnected(user);
            }
        },
        [setIsConnected, trackEvent],
    );

    const { load, isLoaded: googleScriptLoaded } = useGoogleScript(
        'google-one-tap',
        onLogin,
        undefined,
        onRegister,
    );

    useEffect(() => {
        if (!isUserConnectedOnLoad) {
            load().catch((error) => {
                logger.error('Google one tap could not init', {}, error);
            });
        }
    }, [load, isUserConnectedOnLoad]);

    useEffect(() => {
        if (!isUserConnectedOnLoad && googleScriptLoaded && window.google) {
            (window.google as WindowGoogle).accounts.id.prompt();
        }
    }, [googleScriptLoaded, isUserConnectedOnLoad]);

    return null;
}
